import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { EditableTitle } from 'src/components/EditableTitle';
import { Heading } from 'src/components/Heading';
import { Loading } from 'src/components/Loading';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNavigation } from 'src/contexts/NavigationContext';
import { getSafeReturnToPath } from 'src/utils/returnTo';
import { days } from './constants';
import {
  ContactWindowProvider,
  useContactWindow,
} from './ContactWindowContext';
import { TimezoneSettings } from './TimezoneSettings';
import { WeeklyContactWindow } from './WeeklyContactWindow';

const FALLBACK_RETURN_PATH = '/beta/campaigns/contact-windows';

function ContactWindowContent() {
  const { navigateWithBlocking } = useNavigation();
  const {
    save,
    description,
    label,
    setLabel,
    setDescription,
    selectedDays,
    isInitializing,
  } = useContactWindow();
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const { environmentData } = useEnvironment();
  const [searchParams] = useSearchParams();

  const returnTo = searchParams.get('returnTo');
  const requestedContactWindowId = searchParams.get('id');

  const getReturnUrl = useMemo(() => {
    return (scheduleId: string | null) => {
      const safeReturnToPath = getSafeReturnToPath(
        returnTo,
        FALLBACK_RETURN_PATH,
      );
      const url = new URL(safeReturnToPath, window.location.origin);
      if (scheduleId) {
        url.searchParams.set('campaignSchedule', scheduleId);
      }

      return `${url.pathname}${url.search}${url.hash}`;
    };
  }, [returnTo]);

  const handleCancel = () =>
    navigateWithBlocking(getReturnUrl(requestedContactWindowId));

  const handleSaveExit = async () => {
    const campaignScheduleId = await save();
    if (!campaignScheduleId) return;
    navigateWithBlocking(getReturnUrl(campaignScheduleId));
  };

  const handleSave = async () => {
    const savedContactWindowId = await save();
    if (!savedContactWindowId) return;

    if (!requestedContactWindowId) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('id', savedContactWindowId);
      const search = nextSearchParams.toString();
      // Self-navigate to the same route to persist `id=<newId>` in the URL.
      // This makes refresh (and future loads) fetch the saved record by ID.
      navigateWithBlocking(
        `/beta/campaigns/contact-window${search ? `?${search}` : ''}`,
      );
    }
  };

  const selectedDaysText = useMemo(
    () =>
      days
        .filter((d) => selectedDays.includes(d.value))
        .map((d) => d.label)
        .join(', '),
    [selectedDays],
  );

  if (!environmentData || isInitializing) {
    return (
      <div className="flex gap-2 items-center justify-center py-12">
        <Loading size="8" />
        <p className="text-neutral-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex-1">
      <Heading
        title={
          <EditableTitle
            titleInputRef={titleInputRef}
            isEditing={isEditingDetails}
            setIsEditing={setIsEditingDetails}
            name={label}
            description={description}
            onNameChange={setLabel}
            onDescriptionChange={setDescription}
            titlePlaceholder="Campaign schedule"
            subtitlePlaceholder="Add description"
          />
        }
        subtitle={selectedDaysText}
      >
        <div className="flex gap-2 mt-3">
          <Button variant="outlined" size="small" onClick={handleCancel}>
            Cancel
          </Button>

          <Button variant="outlined" size="small" onClick={handleSave}>
            Save
          </Button>
          <Button size="small" onClick={handleSaveExit}>
            Save & Exit
          </Button>
        </div>
      </Heading>
      <ContentWrapper className="space-y-6">
        <WeeklyContactWindow />
        <TimezoneSettings />
      </ContentWrapper>
    </div>
  );
}

export default function ContactWindow() {
  return (
    <ContactWindowProvider>
      <ContactWindowContent />
    </ContactWindowProvider>
  );
}
